import MakeShape from "./MakeShape";
import debug from "debug";
import Experience from "../Experience";
import MakeGroup from "./MakeGroup";
import MakeGroup3D from "./MakeGroup3D";
import cascadeVault from "./CascadeVault";
import MakePartProperties from "./MakePartProperties";
const log = debug("app:3d");

/**
 * The `MakePart` class is responsible for initializing and managing parts in the scene.
 * It fetches part data, processes textures and properties, and emits relevant events.
 * @category Experience
 * @subcategory World
 */
export default class MakePart {
  /**
   * Constructs an instance of `MakePart` and initializes required dependencies.
   */
  constructor() {
    try {
      this.experience = new Experience();
      this.eventHub = this.experience.eventHub;
    } catch (error) {
      console.error("Error in MakePart constructor:", error);
    }
    this.makePartProperties = new MakePartProperties(this.experience);
  }

  cleanup() {
    try {
        if (this.eventHub) {
            this.eventHub.off("xmd");
        }

        this.figure = null;
        this.viewMode = null;
        this.version = null;

        log("Cleanup completed successfully.");
    } catch (error) {
        console.error("Error during cleanup:", error);
    }
}

  /**
   * Initializes the part creation process.
   * @param {object} figure - The figure object containing part details.
   * @param {string} viewMode - The view mode in which the part should be rendered.
   * @param {string} version - The version of the part data.
   * @returns {Promise<THREE.Group>} - A promise that resolves to the created group.
   */
  async init(figure, viewMode, version) {
    log("version", version);
    log("viewMode", viewMode);
    log("figure", figure);
    try {
      this.figure = figure;
      this.viewMode = viewMode;
      this.version = version;
      const params = await this.fetchPartData();
      log("params", params);
      
      // Ensure params is defined before passing to MakeShape
      if (!params) {
        log("Warning: fetchPartData returned undefined, creating minimal params");
        const minimalParams = {
          mode: this.figure.partData?.mode || "xm-part",
          partNumber: this.figure.partData?.fullpartnumber || this.figure.partData?.partNumber || "",
          x: this.figure.x || 0,
          z: this.figure.z || 0,
          layer: parseFloat(this.figure.partData?.layer) || 30,
          angle: this.figure.angle || 0,
          viewMode: this.viewMode || "hybrid",
          version: parseFloat(this.version) || 2.0,
          properties: {},
          textures: {},
          w: 0,
          h: 0,
          ports: [],
        };
        log("Using minimal params:", minimalParams);
        const shape = await new MakeShape().init(minimalParams);
        return null; // Return null to skip this part rather than crash
      }
      
      const shape = await new MakeShape().init(params);

      const group = viewMode["3d"]
        ? new MakeGroup3D().init(params, shape)
        : new MakeGroup().init(params, shape);
      this.eventHub.emit("xmd");

      return group;
    } catch (error) {
      console.error("Error in MakePart init:", error);
    }
  }

  /**
   * Fetches data from a given map with retries.
   * @param {string} key - The key to search in the map.
   * @param {Map} map - The map containing the data.
   * @param {number} [maxAttempts=5] - The maximum number of attempts.
   * @param {number} [delay=50] - The delay between attempts in milliseconds.
   * @returns {Promise<any>} - The fetched data or null if not found.
   */
  async fetchDataFromMap(key, map, maxAttempts = 5, delay = 50) {
    try {
      let attempts = 0;

      while (attempts < maxAttempts) {
        if (map.has(key)) {
          const value = map.get(key);
          if (value !== undefined) {
            return value;
          }
        }
        attempts++;
        if (attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
      log(
        `Failed to fetch data for key: ${key} after ${maxAttempts} attempts.`
      );
      return null;
    } catch (error) {
      console.error("Error in fetchDataFromMap:", error);
    }
  }

  /**
   * Fetches part data based on the mode and properties of the figure.
   * @returns {Promise<object>} - A promise that resolves to the part data.
   */
  async fetchPartData() {
    try {
      const parts = this.experience.dataVault.parts;
      const formFactors = this.experience.dataVault.formFactors;

      let mode = this.figure.partData.mode;
      let params;

      //CFG MODE
      if (mode === "user-cfg") {
        if (this.version == 1.0) {
          log("version 1.0 in process");
          const formFactorIndex = this.figure.partData.formFactor.index;
          const partCategory = this.figure.partData.category;

          const formFactorId =
            this.makePartProperties.findFfIdByCategoryAndIndex(
              cascadeVault,
              partCategory,
              formFactorIndex
            );
          params = await this.fetchPartDataByFormFactor(
            formFactorId,
            formFactors
          );
          log("params 1.0", params);
        } else if (this.version == 2.0) {
          const partData = this.figure.partData;
          log("version 2.0 in process - partData", partData);
          const formFactorId =
            partData.properties?.["FormFactor"] ||
            partData.formFactor ||
            partData.ffId;

          log("params 2.0 -> partData, formFactorId", partData, formFactorId);
          if (formFactorId) {
            params = await this.fetchPartDataByFormFactor(
              formFactorId,
              formFactors
            );
            log("params 2.0", params);
          } else {
            log("error in provided figure data");
          }
        }
      }
      //XM MODE
      else if (mode === "xm-part" || mode === "cfg-part") {
        const partNumber =
          this.figure.partData.fullpartnumber ||
          this.figure.partData.partNumber ||
          false;
        if (partNumber) {
          params = await this.fetchPartDataByPartNumber(
            partNumber,
            parts,
            formFactors
          );
          log("pramas", params);
        } else {
          log("error in provided figure data");
        }
      }
      //other cases
      else {
        log("error in provided figure data - unexpected mode:", mode);
      }
      return params || null; // Return null instead of undefined
    } catch (error) {
      console.error("Error in fetchPartData:", error);
      return null; // Return null instead of undefined
    }
  }

  /**
   * Fetches part data using a part number.
   * @param {string} partNumber - The part number to search.
   * @param {Map} parts - The parts data map.
   * @param {Map} formFactors - The form factors data map.
   * @returns {Promise<object>} - The fetched part data.
   */
  async fetchPartDataByPartNumber(partNumber, parts, formFactors) {
    try {
      const part = await this.fetchDataFromMap(partNumber, parts);
      log("Part", part);
      if (part) {
        const formFactor = await this.fetchDataFromMap(part.ffId, formFactors);
        log("part and formfactor", part, formFactor);
        if (!formFactor) {
          log("Form factor not found for part:", part.ffId);
          return null;
        }
        const params = await this.prepareParams({ part, formFactor });
        log("Params", params);
        return params;
      } else {
        return this.fetchPartDataByMissingPartNumber(
          partNumber,
          parts,
          formFactors
        );
      }
    } catch (error) {
      console.error("Error in fetchPartDataByPartNumber:", error);
      return null;
    }
  }

  /**
   * Fetches part data using a form factor ID.
   * @param {string} formFactorId - The form factor ID.
   * @param {Map} formFactors - The form factors data map.
   * @returns {Promise<object>} - The fetched part data.
   */
  async fetchPartDataByFormFactor(formFactorId, formFactors) {
    try {
      const formFactor = await this.fetchDataFromMap(formFactorId, formFactors);
      log("formFactorez", formFactor);
      if (formFactor) {
        const part = {};
        const params = await this.prepareParams({ part, formFactor });
        log("Params", params);
        return params;
      } else {
        const partNumber =
          this.figure.partData?.fullpartnumber ||
          this.figure.partData?.partNumber ||
          "";
        const parts = this.experience.dataVault?.parts;
        return this.fetchPartDataByMissingPartNumber(
          partNumber,
          parts || new Map(),
          formFactors
        );
      }
    } catch (error) {
      console.error("Error in fetchPartDataByFormFactor:", error);
    }
  }

  /**
   * Attempts to fetch part data when a part number is missing.
   * @param {string} partNumber - The missing part number.
   * @param {Map} parts - The parts data map.
   * @param {Map} formFactors - The form factors data map.
   * @returns {Promise<object>} - The fetched part data.
   */
  async fetchPartDataByMissingPartNumber(partNumber, parts, formFactors) {
    try {
      log("this.figure", this.figure);
      let formFactorId;
      this.figure.partData.mode = "user-cfg";
      if (this.version === "1.0") {
        const formFactorIndex = this.figure.partData.formFactor.index;
        const partCategory = this.figure.partData.category;

        log(
          "missing - formfactorIndex, partCategoy",
          formFactorIndex,
          partCategory
        );

        formFactorId = this.makePartProperties.findFfIdByCategoryAndIndex(
          cascadeVault,
          partCategory,
          formFactorIndex
        );

        log("missing - formfactorId", formFactorId);
      }

      if (!formFactorId) {
        formFactorId =
          this.figure.partData.formFactor ||
          this.figure.partData.properties["Form Factors"] ||
          this.figure.partData.properties["FormFactors"];
      }

      const formFactor = await this.fetchDataFromMap(formFactorId, formFactors);
      log("missing - formfactor", formFactor);

      if (formFactor) {
        const part = {};
        const params = await this.prepareParams({ part, formFactor });
        log("missing - Params", params);
        return params;
      } else {
        log("Form factor not found in fetchPartDataByMissingPartNumber");
        return null;
      }
    } catch (error) {
      console.error("Error in fetchPartDataByMissingPartNumber:", error);
      return null;
    }
  }

  /**
   * Prepares parameters for part creation based on fetched data.
   * @param {object} data - The part and form factor data.
   * @param {object} data.part - The part data.
   * @param {object} data.formFactor - The form factor data.
   * @returns {Promise<object>} - The prepared parameters.
   */
  async prepareParams({ part, formFactor }) {
    try {
      log("1 starting preparing params", this.version, this.viewMode);
      if (
        this.version === "1.0" &&
        "original" in this.figure.partData &&
        this.figure.partData.original === false
      ) {
        this.figure.partData.mode = "cfg-part";
        log("Mode updated to cfg-part as original is false for version 1.0");
      }

      let texture, params;
      const brdTextureKey = part?.images?.[0]?.imageURL || "";
      const hybTextureKey = formFactor.images[0].imageURL;
      const cfgTextureKey = formFactor.images[1].imageURL;
      const properties = this.makePartProperties.processProperties(
        formFactor.catId,
        part,
        this.version,
        this.figure
      );
      log("assigned basic textures");
      if (this.figure.partData.mode === "user-cfg") {
        texture = {
          brd: cfgTextureKey,
          hyb: cfgTextureKey,
          cfg: cfgTextureKey,
        };

        log("we are in the user-cfg mode", texture);
        params = {
          mode: this.figure.partData.mode || "user-cfg",
          category: formFactor.catId || undefined,
          partNumber:
            this.figure.partData.fullpartnumber ||
            this.figure.partData.partNumber ||
            this.figure.partData.properties["Part Number"] ||
            "enter Part Number",
          objectId: this.figure.partData.objectId || "",
          mfgPart:
            this.figure.partData.properties?.["Manufacturer Part Number"] ||
            this.figure.partData.mfgPart ||
            "enter Manufacturer PN",
          formFactor: formFactor.ffIdx,
          properties: properties,
          w: formFactor.w,
          h: formFactor.h,
          textures: texture,
          ports: formFactor.ports,
          x: this.figure.x,
          z: this.figure.z,
          layer: parseFloat(formFactor.layer) || 30,
          angle: this.figure.angle,
          viewMode: this.viewMode || "hybrid",
          version: parseFloat(this.version),
        };
      } else if (
        this.figure.partData.mode === "xm-part" ||
        this.figure.partData.mode === "cfg-part"
      ) {
        log("partdata mode:", this.figure.partData.mode);
        log("xm-part mode: partData", this.figure.partData);
        log("xm-part mode: part", part);
        texture = {
          brd: brdTextureKey,
          hyb: hybTextureKey,
          cfg: cfgTextureKey,
        };

        // For cfg-part mode, use properties from partData.properties if available
        const isCfgPart = this.figure.partData.mode === "cfg-part";

        params = {
          mode: this.figure.partData.mode || "xm-part",
          category: part.catId || undefined,
          partNumber: isCfgPart
            ? (this.figure.partData.properties?.["Part Number"] ||
               this.figure.partData.fullpartnumber ||
               this.figure.partData.partNumber ||
               part.id)
            : part.id,
          objectId: this.figure.partData.objectId || "",
          mfgPart: isCfgPart
            ? (this.figure.partData.properties?.["Manufacturer Part Number"] ||
               this.figure.partData.mfgPart ||
               part.mfgPart)
            : part.mfgPart,
          formFactor: part.ffId || undefined,
          properties: properties,
          w: formFactor.w,
          h: formFactor.h,
          textures: texture,
          glb: part.glb || "",
          ports: formFactor.ports,
          x: this.figure.x,
          z: this.figure.z,
          layer: parseFloat(part.layer),
          angle: this.figure.angle,
          viewMode: this.viewMode || "hybrid",
          price: part.price,
          ctlBoard: part.ctlBoard,
          version: parseFloat(this.version),
        };
      } else {
        log("unexpected mode:", this.figure.partData.mode);
        // Return a minimal params object to prevent undefined errors
        params = {
          mode: this.figure.partData.mode || "xm-part",
          partNumber: this.figure.partData.fullpartnumber || this.figure.partData.partNumber || "",
          x: this.figure.x,
          z: this.figure.z,
          layer: parseFloat(this.figure.partData.layer) || 30,
          angle: this.figure.angle,
          viewMode: this.viewMode || "hybrid",
          version: parseFloat(this.version) || 2.0,
          properties: {},
          textures: {},
          w: 0,
          h: 0,
          ports: [],
        };
      }
      return params;
    } catch (error) {
      console.error("Error in prepareParams:", error);
      // Return a minimal params object even on error to prevent undefined errors
      return {
        mode: this.figure.partData?.mode || "xm-part",
        partNumber: this.figure.partData?.fullpartnumber || this.figure.partData?.partNumber || "",
        x: this.figure.x || 0,
        z: this.figure.z || 0,
        layer: parseFloat(this.figure.partData?.layer) || 30,
        angle: this.figure.angle || 0,
        viewMode: this.viewMode || "hybrid",
        version: parseFloat(this.version) || 2.0,
        properties: {},
        textures: {},
        w: 0,
        h: 0,
        ports: [],
      };
    }
  }
}
